import { useRef, useState, useEffect } from 'react';
import api from '../../api/client';
import { UploadProgress, UploadProgressList } from './UploadProgress';

const CONCURRENCY = 3;

export default function FileUploader({
  onUpload,
  onDuplicate,
  onBatchComplete,
  accept = 'audio/*',
  multiple = false,
  maxSizeMB = 500,
  label = 'Subir archivos',
  pollMediaStatus = false,
}) {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [summary, setSummary] = useState(null); // { total, done, failed, running }
  const queueRef = useRef([]);
  const activeRef = useRef(0);
  const countersRef = useRef({ done: 0, failed: 0 });
  const intervalsRef = useRef([]);

  useEffect(() => {
    return () => {
      intervalsRef.current.forEach((id) => clearInterval(id));
    };
  }, []);

  const startMediaStatusPolling = (uploadId, mediaId) => {
    const poll = async () => {
      try {
        const { data } = await api.get(`/api/media/${mediaId}`);
        const st = data.processing_status;
        if (st === 'ready' || st === 'error') {
          setUploads((prev) => prev.map((u) => (u.id === uploadId ? { ...u, progress: 100, status: st } : u)));
          setTimeout(() => {
            setUploads((prev) => prev.filter((u) => u.id !== uploadId));
          }, 2500);
          return true;
        }
        setUploads((prev) => prev.map((u) => (u.id === uploadId ? { ...u, status: 'processing' } : u)));
        return false;
      } catch {
        return false;
      }
    };
    const interval = setInterval(async () => {
      if (await poll()) clearInterval(interval);
    }, 3000);
    intervalsRef.current.push(interval);
    poll().then((done) => { if (done) clearInterval(interval); });
  };

  const maxSize = maxSizeMB * 1024 * 1024;

  const finishIfEmpty = () => {
    if (activeRef.current === 0 && queueRef.current.length === 0) {
      const c = countersRef.current;
      setSummary((prev) => ({ ...prev, done: c.done, failed: c.failed, running: 0, finished: true }));
      if (onBatchComplete) {
        setTimeout(() => onBatchComplete(c.done, c.failed), 0);
      }
      setTimeout(() => setSummary(null), 8000);
      countersRef.current = { done: 0, failed: 0 };
    }
  };

  const processNext = () => {
    if (activeRef.current >= CONCURRENCY) return;
    const next = queueRef.current.shift();
    if (!next) {
      finishIfEmpty();
      return;
    }
    activeRef.current += 1;
    setSummary((prev) => prev && { ...prev, running: activeRef.current });

    const { file, uploadId } = next;

    onUpload(file, uploadId, (progress) => {
      setUploads((prev) => prev.map((u) => (u.id === uploadId ? { ...u, progress: Math.round(progress) } : u)));
    })
      .then((response) => {
        countersRef.current.done += 1;
        if (pollMediaStatus && response?.data?.id) {
          setUploads((prev) => prev.map((u) => (u.id === uploadId ? { ...u, progress: 100, status: 'processing' } : u)));
          startMediaStatusPolling(uploadId, response.data.id);
        } else {
          setUploads((prev) => prev.map((u) => (u.id === uploadId ? { ...u, progress: 100, status: 'ready' } : u)));
          setTimeout(() => {
            setUploads((prev) => prev.filter((u) => u.id !== uploadId));
          }, 2500);
        }
      })
      .catch((err) => {
        if (err.response?.status === 409 && onDuplicate) {
          setUploads((prev) => prev.filter((u) => u.id !== uploadId));
          onDuplicate(uploadId, file, err.response.data);
          countersRef.current.failed += 1;
        } else {
          countersRef.current.failed += 1;
          setUploads((prev) => prev.map((u) => (u.id === uploadId ? { ...u, status: 'error' } : u)));
          setTimeout(() => {
            setUploads((prev) => prev.filter((u) => u.id !== uploadId));
          }, 3000);
        }
      })
      .finally(() => {
        activeRef.current -= 1;
        setSummary((prev) => prev && { ...prev, done: countersRef.current.done, failed: countersRef.current.failed, running: activeRef.current });
        processNext();
      });
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const validFiles = fileArray.filter((f) => f.size <= maxSize);
    const invalidFiles = fileArray.filter((f) => f.size > maxSize);

    if (invalidFiles.length > 0) {
      alert(`Algunos archivos superan el tamaño máximo de ${maxSizeMB}MB y se omitirán.`);
    }
    if (validFiles.length === 0) return;

    countersRef.current = { done: 0, failed: 0 };
    setSummary({ total: validFiles.length, done: 0, failed: 0, running: 0, finished: false });

    for (const file of validFiles) {
      const uploadId = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString() + Math.random();
      setUploads((prev) => [...prev, { id: uploadId, file, progress: 0, status: 'uploading' }]);
      queueRef.current.push({ file, uploadId });
    }
    for (let i = 0; i < CONCURRENCY; i++) {
      processNext();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  const progressPct = summary && summary.total > 0
    ? Math.round(((summary.done + summary.failed) / summary.total) * 100)
    : 0;

  return (
    <div className="space-y-4">
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-all ${
          isDragging ? 'border-primary-500 bg-dark-800/50' : 'border-dark-700 hover:border-dark-600'
        }`}
      >
        <input ref={inputRef} type="file" onChange={handleFileChange} multiple={multiple} accept={accept} className="hidden" />
        <svg className="mx-auto mb-4 h-12 w-12 text-dark-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115 7M15 19h3a2 2 0 002-2v-7m-7 0h-2m0 0V9a5 5 0 00-5-5C7.306 4 5 6.306 5 9c0 2.353 1.405 4.274 3.5 5h7.5z" />
        </svg>
        <span className="text-sm font-medium text-dark-100">{label}</span>
        <p className="mt-2 text-xs text-dark-500">
          Podés seleccionar todos los archivos que quieras · Tamaño máximo por archivo: {maxSizeMB}MB · Se suben de a {CONCURRENCY}
        </p>
      </div>

      {/* Resumen de subida */}
      {summary && summary.total > 0 && (
        <div className="rounded-lg bg-dark-800/60 border border-dark-700 p-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-dark-200">
              {summary.finished
                ? (summary.failed === 0
                    ? `✅ ${summary.done} archivo(s) subido(s) correctamente`
                    : `⚠️ ${summary.done} subidos, ${summary.failed} con error`)
                : `Subiendo ${summary.done + summary.failed + summary.running} de ${summary.total}...`}
            </span>
            {!summary.finished && (
              <span className="text-xs text-dark-400">{progressPct}%</span>
            )}
          </div>
          {!summary.finished && (
            <div className="mt-2 h-1.5 w-full rounded-full bg-dark-700 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${summary.failed > 0 ? 'bg-warning-500' : 'bg-primary-500'}`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          )}
        </div>
      )}

      {uploads.length > 0 && <UploadProgressList uploads={uploads} />}
    </div>
  );
}
